import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeads } from '../context/LeadsContext';
import { useTasks } from '../context/TasksContext';
import { useRole } from '../context/RoleContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ClipboardList, Send, CheckCircle2, ExternalLink, ChevronDown, ChevronUp, X, MessageSquare, Truck, AlertCircle } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { db } from '../firebase';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import StandaloneQuickQuoteModal from '../components/StandaloneQuickQuoteModal';
import StripeConnectBanner from '../components/StripeConnectBanner';
import AutomationImpactTile from '../components/AutomationImpactTile';
import SchedulingAttentionTile from '../components/SchedulingAttentionTile';
import { useUnreadCount } from '../hooks/useUnreadCount';

const API = process.env.REACT_APP_BACKEND_URL;

// Currency formatter — e.g. $1,234.56
const fmt$ = (n) =>
  parseFloat(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Returns true if a Firestore Timestamp or ISO string falls in the current calendar month
const isCurrentMonth = (val) => {
  if (!val) return false;
  try {
    const d = new Date(val?.toDate ? val.toDate() : val);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  } catch { return false; }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { leads } = useLeads();
  const { tasks } = useTasks();
  const { isOwner, isAdmin, isSales, isTechnician, isMarketing } = useRole();
  const { user } = useAuth();
  const { companyId } = useCompany();

  const [companyUsers, setCompanyUsers] = useState([]);
  const [revenue, setRevenue] = useState({
    paidThisMonth: 0,
    outstanding: 0,
    autoChargedPaid: 0,
    paymentLinkPaid: 0,
    prepayPaid: 0,
    autoChargeSuccessCount: 0,
    autoChargeFailedCount: 0,
    attentionNeededCount: 0,
    loading: true,
  });
  const [showQuickQuote, setShowQuickQuote] = useState(false);

  // ── Company setup state for milestone card ─────────────────────────────────
  const [companySetup, setCompanySetup] = useState({ loaded: false, phone: false, stripeConnected: false });
  const [gsCollapsed, setGsCollapsed]   = useState(false);
  const [gsDismissed, setGsDismissed]   = useState(
    () => localStorage.getItem('kro_gs_dismissed') === '1'
  );

  const isManagerLike = isOwner || isAdmin || isSales || isMarketing || (!isTechnician && !isOwner && !isAdmin && !isSales && !isMarketing);

  // Real-time unread count — powers the mobile ops summary card
  const unreadCount = useUnreadCount();

  useEffect(() => {
    const loadUsers = async () => {
      try {
        if (!db || !companyId) {
          setCompanyUsers([]);
          return;
        }
        const membershipsSnapshot = await getDocs(collection(db, 'companies', companyId, 'users'));
        const memberships = membershipsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        const joined = await Promise.all(
          memberships.map(async (m) => {
            try {
              const profileSnap = await getDoc(doc(db, 'users', m.id));
              const profile = profileSnap.exists() ? profileSnap.data() : {};
              return { id: m.id, role: m.role, ...profile };
            } catch (e) {
              return { id: m.id, role: m.role };
            }
          })
        );
        setCompanyUsers(joined);
      } catch (err) {
        console.error('Failed to load company users for dashboard', err);
        setCompanyUsers([]);
      }
    };

    loadUsers();
  }, [companyId]);

  // Load company setup state once for milestone card
  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, 'companies', companyId))
      .then(snap => {
        const d = snap.data() || {};
        setCompanySetup({
          loaded:          true,
          phone:           !!(d.contact?.phone),
          stripeConnected: d.stripeConnectStatus === 'active',
        });
      })
      .catch(() => setCompanySetup({ loaded: true, phone: false, stripeConnected: false }));
  }, [companyId]);

  const dismissGettingStarted = () => {
    localStorage.setItem('kro_gs_dismissed', '1');
    setGsDismissed(true);
  };

  const leadMap = useMemo(() => {
    const map = {};
    leads.forEach((lead) => {
      map[lead.id] = lead;
    });
    return map;
  }, [leads]);



  const assignedUserMap = useMemo(() => {
    const map = {};
    companyUsers.forEach((u) => {
      map[u.id] = u.displayName || u.name || u.email || u.id;
    });
    return map;
  }, [companyUsers]);

  // ── Revenue metrics ─────────────────────────────────────────────────────
  // Bounded server-side aggregation. Replaces the previous unbounded
  // `onSnapshot(collection(invoices))` which streamed the tenant's full
  // invoice history to every open Dashboard tab. Uses:
  //   • initial fetch on mount
  //   • 60s poll while the tab is visible
  //   • immediate refetch on window focus
  const revenuePollRef = useRef(null);
  const fetchRevenueSummary = useCallback(async () => {
    if (!companyId) return;
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.get(`${API}/api/dashboard/revenue-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRevenue({
        paidThisMonth:          res.data.paidThisMonth ?? 0,
        outstanding:            res.data.outstanding ?? 0,
        autoChargedPaid:        res.data.autoChargedPaid ?? 0,
        paymentLinkPaid:        res.data.paymentLinkPaid ?? 0,
        prepayPaid:             res.data.prepayPaid ?? 0,
        autoChargeSuccessCount: res.data.autoChargeSuccessCount ?? 0,
        autoChargeFailedCount:  res.data.autoChargeFailedCount ?? 0,
        attentionNeededCount:   res.data.attentionNeededCount ?? 0,
        loading: false,
      });
    } catch (err) {
      console.error('[Dashboard] Revenue summary fetch error:', err);
      setRevenue(r => ({ ...r, loading: false }));
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    fetchRevenueSummary();
    const start = () => {
      if (revenuePollRef.current) return;
      revenuePollRef.current = setInterval(fetchRevenueSummary, 60_000);
    };
    const stop = () => {
      if (revenuePollRef.current) {
        clearInterval(revenuePollRef.current);
        revenuePollRef.current = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchRevenueSummary();
        start();
      } else {
        stop();
      }
    };
    const onFocus = () => fetchRevenueSummary();
    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [companyId, fetchRevenueSummary]);


  const now = useMemo(() => new Date(), []);
  const todayStart = useMemo(() => new Date(now.getFullYear(), now.getMonth(), now.getDate()), [now]);
  const tomorrowStart = useMemo(() => new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1), [now]);
  const sevenDaysOut = useMemo(() => new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate() + 7), [todayStart]);

  const parseDueDateTime = (task) => {
    if (!task.dueDate) return null;
    const iso = task.dueTime ? `${task.dueDate}T${task.dueTime}:00` : `${task.dueDate}T00:00:00`;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const parseScheduledStart = (task) => {
    if (!task.scheduledStart) return null;
    const d = new Date(task.scheduledStart);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const isTaskAssignedToCurrentUser = useCallback((task) => {
    if (!isTechnician) return true;
    if (!user?.uid) return false;
    return task.assignedTo === user.uid;
  }, [isTechnician, user]);

  // Helper to create a local date key (YYYY-MM-DD) without UTC drift
  const toLocalDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Top summary metrics (manager-like roles only)
  const metrics = useMemo(() => {
    if (!isManagerLike) {
      return {
        newLeads: 0,
        activeQuotes: 0,
        unscheduledJobs: 0,
        scheduledJobsNext7: 0,
        jobsCompletedThisMonth: 0,
      };
    }

    const newLeads = leads.filter((l) => l.stage === 'New Lead').length;
    const activeQuotes = leads.filter((l) => l.stage === 'In Progress' || l.stage === 'Quoted').length;
    const unscheduledJobs = leads.filter((l) => l.stage === 'Approved').length;

    // Count jobs with scheduledStart in next 7 days (regardless of lead stage)
    const scheduledJobsNext7 = tasks.filter((task) => {
      if (task.type !== 'job') return false;
      const start = parseScheduledStart(task);
      if (!start) return false;
      // Use local date comparison
      const startKey = toLocalDateKey(start);
      const todayKey = toLocalDateKey(todayStart);
      const sevenDaysKey = toLocalDateKey(sevenDaysOut);
      return startKey >= todayKey && startKey < sevenDaysKey;
    }).length;

    // Jobs completed in the current calendar month
    // completedAt may be absent on older tasks — fall back to updatedAt gracefully
    const jobsCompletedThisMonth = tasks.filter((task) => {
      if (task.type !== 'job' || task.status !== 'completed') return false;
      return isCurrentMonth(task.completedAt || task.updatedAt);
    }).length;

    return { newLeads, activeQuotes, unscheduledJobs, scheduledJobsNext7, jobsCompletedThisMonth };
  }, [isManagerLike, leads, tasks, todayStart, sevenDaysOut]);

  // Today's Jobs (jobs with scheduledStart today, respecting role visibility).
  // Excludes cancelled / no-show so that
  // Completed + In Progress + Not Started == Scheduled (Today's Operations math).
  const todaysJobs = useMemo(() => {
    const todayKey = toLocalDateKey(todayStart);
    return tasks.filter((task) => {
      if (task.type !== 'job') return false;
      if (task.status === 'cancelled' || task.status === 'no_show') return false;
      if (!isTaskAssignedToCurrentUser(task)) return false;

      const start = parseScheduledStart(task);
      if (!start) return false;
      // Use local date comparison
      return toLocalDateKey(start) === todayKey;
    });
  }, [tasks, todayStart, isTaskAssignedToCurrentUser]);

  // Today's jobs currently running (en route or on site)
  const jobsInProgressToday = useMemo(() =>
    todaysJobs.filter((t) => t.status === 'en_route' || t.status === 'on_site').length,
    [todaysJobs]
  );

  // Today's jobs already completed
  const jobsCompletedToday = useMemo(() =>
    todaysJobs.filter((t) => t.status === 'completed').length,
    [todaysJobs]
  );

  // Today's jobs not yet started (scheduled but not en route / on site / completed).
  // Mutually exclusive with the two counts above so that
  // Completed + In Progress + Not Started == todaysJobs.length.
  const jobsNotStartedToday = useMemo(() =>
    todaysJobs.length - jobsCompletedToday - jobsInProgressToday,
    [todaysJobs.length, jobsCompletedToday, jobsInProgressToday]
  );

  // Pre-compute onboarding completion — hides Getting Started card on mobile when done
  const gsAllDone = useMemo(() =>
    companySetup.loaded && leads.length > 0 && companySetup.phone && companySetup.stripeConnected,
    [companySetup, leads.length]
  );

  // Overdue Internal Tasks (manager-like roles only)
  const overdueInternalTasks = useMemo(() => {
    if (!isManagerLike) return [];
    return tasks.filter((task) => {
      if (task.type === 'job') return false;
      if (task.status !== 'pending') return false;
      const dt = parseDueDateTime(task);
      if (!dt) return false;
      return dt < now;
    });
  }, [tasks, now, isManagerLike]);

  // Upcoming Internal Tasks (next 7 days, manager-like roles only)
  const upcomingInternalTasks = useMemo(() => {
    if (!isManagerLike) return [];
    return tasks.filter((task) => {
      if (task.type === 'job') return false;
      if (task.status !== 'pending') return false;
      const dt = parseDueDateTime(task);
      if (!dt) return false;
      return dt >= todayStart && dt < sevenDaysOut;
    });
  }, [tasks, todayStart, sevenDaysOut, isManagerLike]);

  // Schedule Outlook — next 7 calendar days, COUNTS only (no individual job
  // rows). Reuses the same task-snapshot the rest of the dashboard reads from
  // and respects the technician-only role filter. Excludes cancelled /
  // no-show so the chip count matches what Calendar will display.
  const outlookDays = 7;
  const scheduleOutlook = useMemo(() => {
    const days = [];
    for (let i = 0; i < outlookDays; i += 1) {
      const dayStart = new Date(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate() + i);
      const dayKey = toLocalDateKey(dayStart);

      const count = tasks.reduce((acc, task) => {
        if (task.type !== 'job') return acc;
        if (task.status === 'cancelled' || task.status === 'no_show') return acc;
        if (!isTaskAssignedToCurrentUser(task)) return acc;
        const start = parseScheduledStart(task);
        if (!start) return acc;
        return toLocalDateKey(start) === dayKey ? acc + 1 : acc;
      }, 0);

      days.push({
        key: dayKey,
        weekday: dayStart.toLocaleDateString(undefined, { weekday: 'short' }),
        dayNum: dayStart.getDate(),
        count,
      });
    }
    return days;
  }, [tasks, todayStart, outlookDays, isTaskAssignedToCurrentUser]);

  // Win rate: Booked leads ÷ leads created in last 30 days (best-effort date parsing)
  const winRate = useMemo(() => {
    if (!leads?.length) return null;
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = leads.filter(l => {
      if (!l.createdAt) return true; // include if no creation date
      try {
        const ms = l.createdAt?.toMillis?.() ?? new Date(l.createdAt).getTime();
        return ms >= cutoff;
      } catch { return true; }
    });
    if (recent.length === 0) return null;
    // Win rate: Approved + Booked + Archived count as successful conversions
    const booked = recent.filter(
      l => l.stage === 'Approved' || l.stage === 'Booked' || l.stage === 'Archived'
    ).length;
    return Math.round((booked / recent.length) * 100);
  }, [leads]);

  const handleMetricClick = (type) => {
    if (type === 'scheduledJobs') {
      navigate('/calendar');
    } else {
      navigate('/pipeline');
    }
  };

  const renderAssignedUser = (task) => {
    if (!task.assignedTo) return null;
    const label = assignedUserMap[task.assignedTo] || task.assignedTo;
    return (
      <span className="text-xs text-gray-500 ml-2">· {label}</span>
    );
  };

  return (
    <div className="space-y-6 px-4 py-4 lg:px-0 lg:py-0">
      {/* Desktop header — hidden on mobile (replaced by mobile summary below) */}
      <div className="hidden lg:flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary mb-1">Dashboard</h1>
          <p className="text-muted-foreground text-sm">Read-only operational overview</p>
        </div>
        {isManagerLike && (
          <Button
            className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm"
            onClick={() => setShowQuickQuote(true)}
            data-testid="quick-quote-dashboard-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Quick Quote
          </Button>
        )}
      </div>

      <StandaloneQuickQuoteModal
        open={showQuickQuote}
        onClose={() => setShowQuickQuote(false)}
      />

      {/* ── Mobile Operations Summary ────────────────────────────────────────
           Visible only on mobile (< lg). Replaces the dense KPI/revenue grid
           with 4 decision-focused action cards.                              */}
      <div className="lg:hidden space-y-4 -mx-4 -mt-4 px-4 pt-4 pb-2 border-b border-gray-100 bg-white" data-testid="mobile-ops-summary">
        {/* Date row + Quick Quote shortcut */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Operations</h1>
            <p className="text-sm text-gray-400">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          {isManagerLike && (
            <button
              type="button"
              onClick={() => setShowQuickQuote(true)}
              className="flex items-center gap-1.5 bg-[var(--cb-orange)] text-white text-sm font-semibold px-3 py-2.5 rounded-lg min-h-[44px] active:opacity-80 transition-opacity"
              data-testid="mobile-quick-quote-btn"
            >
              <Send className="h-3.5 w-3.5" />
              Quote
            </button>
          )}
        </div>

        {/* 2×2 action cards */}
        <div className="grid grid-cols-2 gap-3 pb-2">
          {/* Unread Messages → Inbox */}
          <button
            type="button"
            onClick={() => navigate('/inbox')}
            className={`bg-white rounded-xl border p-4 text-left min-h-[100px] active:scale-[0.98] transition-transform ${unreadCount > 0 ? 'border-orange-300 shadow-sm' : 'border-gray-200'}`}
            data-testid="mobile-dash-unread"
          >
            <div className="flex items-center gap-2 mb-3">
              <MessageSquare className={`h-4 w-4 ${unreadCount > 0 ? 'text-orange-500' : 'text-gray-300'}`} />
              <span className="text-xs font-medium text-gray-500">Unread</span>
            </div>
            <p className={`text-3xl font-bold leading-none ${unreadCount > 0 ? 'text-orange-600' : 'text-gray-300'}`}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </p>
            <p className="text-[11px] text-gray-400 mt-1.5">messages</p>
          </button>

          {/* Jobs In Progress → Dispatch */}
          <button
            type="button"
            onClick={() => navigate('/dispatch')}
            className={`bg-white rounded-xl border p-4 text-left min-h-[100px] active:scale-[0.98] transition-transform ${jobsInProgressToday > 0 ? 'border-indigo-200 shadow-sm' : 'border-gray-200'}`}
            data-testid="mobile-dash-in-progress"
          >
            <div className="flex items-center gap-2 mb-3">
              <Truck className={`h-4 w-4 ${jobsInProgressToday > 0 ? 'text-indigo-500' : 'text-gray-300'}`} />
              <span className="text-xs font-medium text-gray-500">In Progress</span>
            </div>
            <p className={`text-3xl font-bold leading-none ${jobsInProgressToday > 0 ? 'text-indigo-600' : 'text-gray-300'}`}>
              {jobsInProgressToday}
            </p>
            <p className="text-[11px] text-gray-400 mt-1.5">jobs today</p>
          </button>

          {/* Completed Today → Jobs */}
          <button
            type="button"
            onClick={() => navigate('/jobs')}
            className={`bg-white rounded-xl border p-4 text-left min-h-[100px] active:scale-[0.98] transition-transform ${jobsCompletedToday > 0 ? 'border-emerald-200 shadow-sm' : 'border-gray-200'}`}
            data-testid="mobile-dash-completed-today"
          >
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className={`h-4 w-4 ${jobsCompletedToday > 0 ? 'text-emerald-500' : 'text-gray-300'}`} />
              <span className="text-xs font-medium text-gray-500">Done Today</span>
            </div>
            <p className={`text-3xl font-bold leading-none ${jobsCompletedToday > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>
              {jobsCompletedToday}
            </p>
            <p className="text-[11px] text-gray-400 mt-1.5">completed</p>
          </button>

          {/* Needs Attention → Jobs */}
          <button
            type="button"
            onClick={() => navigate('/jobs')}
            className={`bg-white rounded-xl border p-4 text-left min-h-[100px] active:scale-[0.98] transition-transform ${metrics.unscheduledJobs > 0 ? 'border-red-200 shadow-sm' : 'border-gray-200'}`}
            data-testid="mobile-dash-needs-attention"
          >
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className={`h-4 w-4 ${metrics.unscheduledJobs > 0 ? 'text-red-500' : 'text-gray-300'}`} />
              <span className="text-xs font-medium text-gray-500">To Schedule</span>
            </div>
            <p className={`text-3xl font-bold leading-none ${metrics.unscheduledJobs > 0 ? 'text-red-600' : 'text-gray-300'}`}>
              {metrics.unscheduledJobs}
            </p>
            <p className="text-[11px] text-gray-400 mt-1.5">approved jobs</p>
          </button>
        </div>
      </div>

      {/* ── Stripe Connect Banner ─────────────────────────────────────────── */}
      {isManagerLike && !companySetup.stripeConnected && companySetup.loaded && (
        <StripeConnectBanner companyId={companyId} />
      )}

      {/* ── Get Set Up Milestone Card ─────────────────────────────────────── */}
      {isManagerLike && !gsDismissed && (() => {
        const milestones = [
          {
            key:    'quote',
            label:  'Send your first quote',
            done:   leads.length > 0,
            cta:    'Send Quick Quote',
            action: () => setShowQuickQuote(true),
          },
          {
            key:    'phone',
            label:  'Add your business phone number',
            done:   companySetup.phone,
            cta:    'Update Profile',
            action: () => navigate('/settings/company'),
          },
          {
            key:    'stripe',
            label:  'Connect Stripe to accept payments',
            done:   companySetup.stripeConnected,
            cta:    'Connect Stripe',
            action: () => navigate('/settings/integrations'),
          },
        ];
        const doneCount  = milestones.filter(m => m.done).length;
        const allDone    = doneCount === milestones.length;

        return (
          <div
            data-testid="getting-started-card"
            className={`rounded-xl border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 overflow-hidden ${gsAllDone ? 'hidden lg:block' : ''}`}
          >
            {/* Card header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-orange-100">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-orange-100 p-1.5">
                  <ClipboardList className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <span className="font-semibold text-gray-900 text-sm">Get set up</span>
                  <span className="ml-2 text-xs text-gray-500">
                    {doneCount}/{milestones.length} complete
                  </span>
                </div>
                {allDone && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" /> All done!
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setGsCollapsed(c => !c)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  title={gsCollapsed ? 'Expand' : 'Collapse'}
                >
                  {gsCollapsed
                    ? <ChevronDown className="w-4 h-4" />
                    : <ChevronUp   className="w-4 h-4" />
                  }
                </button>
                <button
                  onClick={dismissGettingStarted}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                  title="Dismiss"
                  data-testid="gs-dismiss-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Progress bar */}
            {!gsCollapsed && (
              <div className="h-1 bg-orange-100">
                <div
                  className="h-full bg-orange-400 transition-all duration-500"
                  style={{ width: `${(doneCount / milestones.length) * 100}%` }}
                />
              </div>
            )}

            {/* Milestone rows */}
            {!gsCollapsed && (
              <div className="divide-y divide-orange-100">
                {milestones.map(m => (
                  <div key={m.key} className="flex items-center gap-3 px-5 py-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${m.done ? 'bg-emerald-100' : 'bg-white border-2 border-gray-300'}`}>
                      {m.done && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                    </div>
                    <span className={`flex-1 text-sm ${m.done ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                      {m.label}
                    </span>
                    {!m.done && (
                      <button
                        onClick={m.action}
                        data-testid={`gs-action-${m.key}`}
                        className="flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-800 bg-white hover:bg-orange-50 border border-orange-200 px-2.5 py-1.5 rounded-lg transition-colors flex-shrink-0"
                      >
                        {m.cta} <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}

                {/* Optional setup links */}
                <div className="px-5 py-3 bg-white/40">
                  <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Optional setup</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate('/settings/pricing')}
                      data-testid="gs-pricing-btn"
                      className="text-xs text-gray-500 hover:text-orange-600 bg-white border border-gray-200 hover:border-orange-200 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      Set pricing templates
                    </button>
                    <button
                      onClick={() => navigate('/settings/app-config')}
                      data-testid="gs-tax-btn"
                      className="text-xs text-gray-500 hover:text-orange-600 bg-white border border-gray-200 hover:border-orange-200 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      Configure tax settings
                    </button>
                    <button
                      onClick={() => navigate('/settings/team')}
                      data-testid="gs-team-btn"
                      className="text-xs text-gray-500 hover:text-orange-600 bg-white border border-gray-200 hover:border-orange-200 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      Invite team members
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Top summary metrics (manager-like roles only) */}
      {isManagerLike && (
        <div className="hidden lg:grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <button
            type="button"
            onClick={() => handleMetricClick('newLeads')}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-left hover:shadow-md transition-shadow"
          >
            <p className="text-xs font-medium text-gray-500">New Leads</p>
            <p className="mt-1 text-3xl font-bold text-secondary">{metrics.newLeads}</p>
          </button>

          <button
            type="button"
            onClick={() => handleMetricClick('activeQuotes')}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-left hover:shadow-md transition-shadow"
          >
            <p className="text-xs font-medium text-gray-500">Active Quotes</p>
            <p className="mt-1 text-3xl font-bold text-secondary">{metrics.activeQuotes}</p>
          </button>

          <button
            type="button"
            onClick={() => handleMetricClick('unscheduledJobs')}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-left hover:shadow-md transition-shadow"
          >
            <p className="text-xs font-medium text-gray-500">Ready to Schedule</p>
            <p className="mt-1 text-3xl font-bold text-secondary">{metrics.unscheduledJobs}</p>
          </button>

          <button
            type="button"
            onClick={() => handleMetricClick('scheduledJobs')}
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 text-left hover:shadow-md transition-shadow"
          >
            <p className="text-xs font-medium text-gray-500">Scheduled (Next 7 Days)</p>
            <p className="mt-1 text-3xl font-bold text-secondary">{metrics.scheduledJobsNext7}</p>
          </button>

          <div
            className="bg-white p-4 rounded-lg shadow-sm border border-gray-200"
            data-testid="jobs-completed-this-month"
          >
            <p className="text-xs font-medium text-gray-500">Completed This Month</p>
            <p className="mt-1 text-3xl font-bold text-secondary">{metrics.jobsCompletedThisMonth}</p>
          </div>
        </div>
      )}

      {/* Revenue Summary — visible to manager-like roles */}
      {isManagerLike && (
        <>

          {/* Automation Impact rollup — Phase 1 / Step 4 */}
          <AutomationImpactTile />

          {/* Sprint 3 — Schedule Attention (always-rendered operational tile) */}
          <SchedulingAttentionTile />

          {/* Attention Needed — single tile, only when invoices need intervention. */}
          {!revenue.loading && revenue.attentionNeededCount > 0 && (
            <button
              type="button"
              onClick={() => navigate('/invoices?filter=attention')}
              className="hidden lg:flex w-full items-center gap-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-left hover:bg-red-100 hover:border-red-300 transition-colors shadow-sm"
              data-testid="dashboard-attention-tile"
              title="Open Invoices filtered to failed charges"
            >
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">Attention Needed</p>
                <p className="text-sm text-red-900 mt-0.5">
                  <span className="font-bold">{revenue.attentionNeededCount}</span>
                  {' '}invoice{revenue.attentionNeededCount !== 1 ? 's' : ''} need follow-up — retry or resend payment link.
                </p>
              </div>
              <span className="text-xs font-medium text-red-700 hover:underline shrink-0" data-testid="dashboard-attention-cta">
                Review →
              </span>
            </button>
          )}

          <div className="hidden lg:grid gap-4 sm:grid-cols-3" data-testid="revenue-summary">

          {/* Paid This Month — with payment method breakdown + auto-charge health */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-testid="revenue-paid-this-month">
            <p className="text-xs font-medium text-gray-500">Paid This Month</p>
            {revenue.loading ? (
              <div className="mt-2 h-8 w-28 bg-gray-100 rounded animate-pulse" />
            ) : (
              <>
                <p className="mt-1 text-3xl font-bold text-green-600">{fmt$(revenue.paidThisMonth)}</p>

                {/* Payment method breakdown — only shown when there is revenue */}
                {revenue.paidThisMonth > 0 && (
                  <div className="mt-2 space-y-0.5" data-testid="payment-breakdown">
                    {revenue.autoChargedPaid > 0 && (
                      <p className="text-[11px] text-gray-500">
                        Auto-charged: <span className="font-medium text-gray-700">{fmt$(revenue.autoChargedPaid)}</span>
                      </p>
                    )}
                    {revenue.paymentLinkPaid > 0 && (
                      <p className="text-[11px] text-gray-500">
                        Payment link: <span className="font-medium text-gray-700">{fmt$(revenue.paymentLinkPaid)}</span>
                      </p>
                    )}
                    {revenue.prepayPaid > 0 && (
                      <p className="text-[11px] text-gray-500">
                        Prepay: <span className="font-medium text-gray-700">{fmt$(revenue.prepayPaid)}</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Auto-charge health — only shown when at least one auto-charge event exists */}
                {(revenue.autoChargeSuccessCount > 0 || revenue.autoChargeFailedCount > 0) && (
                  <div
                    className="mt-2 pt-2 border-t border-gray-100 space-y-0.5"
                    data-testid="auto-charge-health"
                  >
                    {revenue.autoChargeSuccessCount > 0 && (
                      <p className="text-[11px] text-green-600">
                        Auto-charge success: {revenue.autoChargeSuccessCount}
                      </p>
                    )}
                    {revenue.autoChargeFailedCount > 0 && (
                      <p className="text-[11px] text-amber-600">
                        Failed charges: {revenue.autoChargeFailedCount}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Outstanding */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-testid="revenue-outstanding">
            <p className="text-xs font-medium text-gray-500">Outstanding</p>
            {revenue.loading ? (
              <div className="mt-2 h-8 w-28 bg-gray-100 rounded animate-pulse" />
            ) : (
              <>
                <p className="mt-1 text-3xl font-bold text-amber-600">{fmt$(revenue.outstanding)}</p>
                <button
                  type="button"
                  className="mt-1 text-xs text-[var(--cb-orange)] hover:underline"
                  onClick={() => navigate('/invoices')}
                  data-testid="view-invoices-link"
                >
                  View Invoices →
                </button>
              </>
            )}
          </div>

          {/* Win Rate */}
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200" data-testid="revenue-win-rate">
            <p className="text-xs font-medium text-gray-500">Win Rate (30 days)</p>
            {!leads?.length ? (
              <div className="mt-2 h-8 w-16 bg-gray-100 rounded animate-pulse" />
            ) : (
              <p className="mt-1 text-3xl font-bold text-blue-600">
                {winRate !== null ? `${winRate}%` : '—'}
              </p>
            )}
          </div>

        </div>
        </>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Overdue Internal Tasks */}
        {isManagerLike && (
          <section className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-secondary">Overdue Internal Tasks</h2>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => navigate('/tasks')}
              >
                View All Tasks
              </Button>
            </div>
            {overdueInternalTasks.length === 0 ? (
              <p className="text-xs text-gray-400">No overdue internal tasks.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {overdueInternalTasks.slice(0, 5).map((task) => {
                  const lead = leadMap[task.leadId];
                  const due = parseDueDateTime(task);
                  const dueLabel = due
                    ? due.toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'No due date';
                  return (
                    <li key={task.id} className="flex flex-col border border-red-100 rounded-md px-3 py-2 bg-red-50/40">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-red-700">{task.title}</span>
                        {task.priority && (
                          <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full border border-red-300 text-red-700 uppercase">
                            {task.priority}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center justify-between text-xs text-gray-600">
                        <span>{lead?.name || 'Unknown lead'}</span>
                        <span>{dueLabel}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* Today's Jobs section removed — replaced by the full-width
            "Today's Operations" summary rendered above the 5-day outlook. */}

        {/* Upcoming Internal Tasks (Next 7 Days) */}
        {isManagerLike && (
          <section className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-secondary">Upcoming Internal Tasks (Next 7 Days)</h2>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => navigate('/tasks')}
              >
                View All Tasks
              </Button>
            </div>
            {upcomingInternalTasks.length === 0 ? (
              <p className="text-xs text-gray-400">No upcoming internal tasks.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {upcomingInternalTasks.slice(0, 5).map((task) => {
                  const due = parseDueDateTime(task);
                  const dueLabel = due
                    ? due.toLocaleString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : 'No due date';
                  return (
                    <li key={task.id} className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm text-secondary">{task.title}</span>
                        <span className="text-xs text-gray-500">{dueLabel}</span>
                      </div>
                      {renderAssignedUser(task)}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}
      </div>

      {/* ── Today's Operations ───────────────────────────────────────────────
          High-level summary of today's job pipeline. Counts only — no
          individual job rows. Scales cleanly from 1 truck to 300+ services
          per day. Detailed management lives in Dispatch.
          Math invariant: Completed + In Progress + Not Started === Scheduled. */}
      <section
        className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-3"
        data-testid="today-ops-summary"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-secondary">Today&apos;s Operations</h2>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => navigate('/dispatch')}
            data-testid="today-ops-open-dispatch"
          >
            Open Dispatch
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div
            className="rounded-md border border-gray-200 px-3 py-3 text-center"
            data-testid="today-ops-scheduled"
          >
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Scheduled</p>
            <p className="mt-1 text-2xl font-bold text-secondary">{todaysJobs.length}</p>
          </div>

          <div
            className="rounded-md border border-emerald-100 bg-emerald-50/40 px-3 py-3 text-center"
            data-testid="today-ops-completed"
          >
            <p className="text-[11px] font-medium text-emerald-700 uppercase tracking-wide">Completed</p>
            <p className="mt-1 text-2xl font-bold text-emerald-700">{jobsCompletedToday}</p>
          </div>

          <div
            className="rounded-md border border-indigo-100 bg-indigo-50/40 px-3 py-3 text-center"
            data-testid="today-ops-in-progress"
          >
            <p className="text-[11px] font-medium text-indigo-700 uppercase tracking-wide">In Progress</p>
            <p className="mt-1 text-2xl font-bold text-indigo-700">{jobsInProgressToday}</p>
          </div>

          <div
            className="rounded-md border border-gray-200 px-3 py-3 text-center"
            data-testid="today-ops-not-started"
          >
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Not Started</p>
            <p className="mt-1 text-2xl font-bold text-secondary">{jobsNotStartedToday}</p>
          </div>
        </div>
      </section>

      {/* ── Schedule Outlook (Next 7 Days) ───────────────────────────────────
          One chip per day, count only — never a list of jobs. Reuses the
          same Firestore task snapshot the rest of the dashboard reads from.
          Detailed schedule management lives in Calendar. */}
      <section
        className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 space-y-3"
        data-testid="schedule-outlook"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-secondary">Schedule Outlook (Next 7 Days)</h2>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => navigate('/calendar')}
            data-testid="schedule-outlook-open-calendar"
          >
            Open Calendar
          </Button>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {scheduleOutlook.map((day) => {
            const hasJobs = day.count > 0;
            return (
              <div
                key={day.key}
                className={`rounded-md border px-2 py-2 text-center ${hasJobs ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'}`}
                data-testid={`schedule-outlook-day-${day.key}`}
              >
                <p className="text-[11px] font-medium text-gray-500 uppercase">{day.weekday}</p>
                <p className="text-[11px] text-gray-400">{day.dayNum}</p>
                <p className={`mt-1 text-xl font-bold ${hasJobs ? 'text-secondary' : 'text-gray-300'}`}>
                  {day.count}
                </p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;